const COLLECTIONS_SOURCE = 'collections'

const asArray = (value) => (Array.isArray(value) ? value : [])

export const titleizeAgent = (value) => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase())

export function agentActivityPresentation(latest) {
  if (!latest) return null
  const eventType = latest.event_type || latest.entry_type || 'agent_activity'
  return {
    label: latest.label || `${titleizeAgent(eventType)} processed`,
    eventType,
    timestamp: latest.timestamp || null,
    fixture: Boolean(latest.fixture),
  }
}

export function agentTelemetryLimitations(activity = {}, queue = {}) {
  const limitations = [...asArray(activity.limitations), ...asArray(queue.limitations)]
    .filter((value) => typeof value === 'string' && value.trim())
  return [...new Set(limitations)]
}

function normalizeHealth(agent) {
  const status = agent?.health?.status || agent?.status
  if (['enabled', 'degraded', 'disabled'].includes(status)) {
    return {
      status,
      status_reasons: asArray(agent.health?.status_reasons || agent.status_reasons),
    }
  }
  return {
    status: 'degraded',
    status_reasons: ['Runtime health was not reported by the Collections API.'],
  }
}

function normalizeCollectionsAgent(agent) {
  const key = agent.key || agent.name
  const nestedModel = agent.model && typeof agent.model === 'object' ? agent.model : null
  const model = nestedModel || {
    provider: agent.provider || agent.model_configuration?.provider || null,
    model: typeof agent.model === 'string' ? agent.model : agent.model_configuration?.model || null,
    model_configuration: agent.model_configuration || null,
  }
  return {
    ...agent,
    key,
    roster_id: `${COLLECTIONS_SOURCE}:${key}`,
    name: key,
    display_name: agent.display_name || titleizeAgent(key),
    tool_name: agent.tool_name || key,
    source: COLLECTIONS_SOURCE,
    source_label: 'Collections system',
    system_owned: true,
    custom: false,
    visibility: agent.visibility || 'user_facing',
    parent_agent: agent.parent_agent || null,
    child_keys: asArray(agent.children),
    children: [],
    capabilities: asArray(agent.capabilities),
    tools: asArray(agent.tools),
    deterministic_effects: asArray(agent.deterministic_effects),
    deep_links: asArray(agent.deep_links),
    model,
    health: normalizeHealth(agent),
  }
}

function normalizeGenericAgent(agent) {
  const key = agent.name || agent.key
  return {
    ...agent,
    key,
    roster_id: `agentframe:${key}`,
    name: key,
    display_name: agent.display_name || titleizeAgent(key),
    source: 'agentframe',
    source_label: agent.custom ? 'Custom agent' : 'Agentframe registry',
    system_owned: false,
    visibility: 'user_facing',
    children: [],
    capabilities: asArray(agent.capabilities),
    health: {
      status: agent.enabled === false ? 'disabled' : 'enabled',
      status_reasons: [],
    },
  }
}

export function buildAgentRoster(genericAgents = [], collectionsPayload = null) {
  const generic = asArray(genericAgents).map(normalizeGenericAgent)
  const collections = asArray(collectionsPayload?.items).map(normalizeCollectionsAgent)
  const byKey = new Map(collections.map((agent) => [agent.key, agent]))

  collections.forEach((agent) => {
    const declared = agent.child_keys.map((key) => byKey.get(key)).filter(Boolean)
    const parented = collections.filter((candidate) => candidate.parent_agent === agent.key)
    agent.children = Array.from(new Map([...declared, ...parented].map((child) => [child.key, child])).values())
  })

  const userFacing = collections.filter((agent) => agent.visibility !== 'internal' && !agent.parent_agent)
  const groups = [
    { key: 'agentframe', label: 'Agentframe registry', description: 'General and custom agents', agents: generic },
    { key: COLLECTIONS_SOURCE, label: 'Collections system', description: 'System-owned operational agents', agents: userFacing },
  ].filter((group) => group.agents.length > 0)

  return {
    groups,
    agents: groups.flatMap((group) => group.agents),
    visibleCount: generic.length + userFacing.length,
    collectionsTotal: collections.length,
  }
}

export function canManageAgent(agent) {
  return Boolean(agent?.custom) && !agent?.system_owned
}

export async function fetchCollectionsAgentRegistry(fetchImpl = fetch, signal) {
  try {
    const response = await fetchImpl('/api/collections/agents', { signal })
    if (!response.ok) {
      return {
        data: null,
        availability: 'unavailable',
        reason: response.status === 404
          ? 'Collections agent reporting is not installed on this backend.'
          : `Collections agent reporting is unavailable (${response.status}).`,
      }
    }
    const data = await response.json()
    return { data, availability: 'available', reason: null }
  } catch (error) {
    if (error?.name === 'AbortError') throw error
    return {
      data: null,
      availability: 'unavailable',
      reason: 'Collections agent reporting could not be reached.',
    }
  }
}
